/**
 * Natural Language Query Panel Component
 * Query Excel data using natural language
 */

import React, { useState } from 'react';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorDisplay } from '../common/ErrorDisplay';
import type { NLQueryResult, QueryFilter } from '@excel-ai-studio/shared';
import '../common/PreviewPanel.css';

interface NLQueryPanelProps {
  data?: unknown[][];
  headers?: string[];
  onQueryExecuted?: (result: NLQueryResult) => void;
}

/**
 * NL Query Panel Component
 */
export const NLQueryPanel: React.FC<NLQueryPanelProps> = ({ data, headers, onQueryExecuted }) => {
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<NLQueryResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showInterpretation, setShowInterpretation] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const PAGE_SIZE = 25;

  /**
   * Execute query
   */
  const executeQuery = async () => {
    if (!query.trim() || !data || !headers) {
      setError('Veuillez entrer une requête et assurez-vous que les données sont disponibles');
      return;
    }

    setError(null);
    setIsLoading(true);

    try {
      const response = await fetch('/api/nl-query-engine/execute', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query,
          data,
          headers,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.statusText}`);
      }

      const queryResult: NLQueryResult = await response.json();
      setResult(queryResult);
      setCurrentPage(0);

      if (onQueryExecuted) {
        onQueryExecuted(queryResult);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Get confidence color
   */
  const getConfidenceColor = (confidence: number): string => {
    if (confidence >= 0.8) return '#4CAF50';
    if (confidence >= 0.6) return '#FF9800';
    return '#F44336';
  };

  /**
   * Format results table with pagination
   */
  const renderResultsTable = () => {
    if (!result || result.results.filteredData.length === 0) {
      return <p style={{ color: '#666', fontSize: '13px' }}>Aucun résultat trouvé</p>;
    }

    const totalRows = result.results.filteredData.length;
    const totalPages = Math.ceil(totalRows / PAGE_SIZE);
    const startIdx = currentPage * PAGE_SIZE;
    const displayRows = result.results.filteredData.slice(startIdx, startIdx + PAGE_SIZE);

    return (
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead>
            <tr style={{ backgroundColor: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              {headers?.map((h, idx) => (
                <th key={idx} style={{ padding: '8px', textAlign: 'left', fontWeight: 'bold', borderRight: '1px solid #ddd', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {displayRows.map((row, rowIdx) => (
              <tr key={rowIdx} style={{ borderBottom: '1px solid #eee' }}>
                {(row as unknown[]).map((cell, cellIdx) => (
                  <td key={cellIdx} style={{ padding: '8px', borderRight: '1px solid #eee', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {String(cell ?? '')}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px', fontSize: '12px' }}>
            <span style={{ color: '#666' }}>
              {startIdx + 1}-{Math.min(startIdx + PAGE_SIZE, totalRows)} sur {totalRows} résultats
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={() => setCurrentPage((p) => Math.max(0, p - 1))}
                disabled={currentPage === 0}
                style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', cursor: currentPage === 0 ? 'not-allowed' : 'pointer', opacity: currentPage === 0 ? 0.4 : 1, backgroundColor: '#fff' }}
              >
                Préc.
              </button>
              <span style={{ padding: '4px 8px', color: '#666' }}>
                {currentPage + 1}/{totalPages}
              </span>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={currentPage >= totalPages - 1}
                style={{ padding: '4px 8px', border: '1px solid #ddd', borderRadius: '4px', cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer', opacity: currentPage >= totalPages - 1 ? 0.4 : 1, backgroundColor: '#fff' }}
              >
                Suiv.
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="preview-panel">
      <div className="preview-header">
        <h3>🔎 Requête en langage naturel</h3>
        <p className="preview-subtitle">Interrogez vos données en français</p>
      </div>

      {error && <ErrorDisplay message={error} onDismiss={() => setError(null)} />}

      {!data && (
        <div style={{ padding: '12px', backgroundColor: '#fff3e0', borderRadius: '4px', marginBottom: '12px', fontSize: '13px', color: '#e65100' }}>
          ⚠️ Aucune donnée disponible. Rafraîchissez la page pour charger les données Excel.
        </div>
      )}

      {!result ? (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', marginBottom: '4px' }}>
              Entrez votre requête
            </label>
            <textarea
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && e.ctrlKey) executeQuery(); }}
              placeholder="ex : 'Afficher les ventes supérieures à 1000' ou 'Calculer la moyenne par catégorie'"
              style={{
                width: '100%', minHeight: '80px', padding: '8px',
                border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px',
                fontFamily: 'Arial, sans-serif', boxSizing: 'border-box', resize: 'vertical',
              }}
            />
            <p style={{ fontSize: '11px', color: '#999', margin: '4px 0 0 0' }}>
              Astuce : Ctrl+Entrée pour exécuter
            </p>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px' }}>Exemples de requêtes :</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {[
                'Afficher tous les enregistrements où les ventes > 1000',
                'Calculer la moyenne des prix par catégorie',
                'Compter les enregistrements triés par date',
                'Afficher les enregistrements contenant "actif"',
              ].map((example, idx) => (
                <button
                  key={idx}
                  onClick={() => setQuery(example)}
                  style={{
                    padding: '8px 12px', backgroundColor: '#f0f0f0', border: '1px solid #ddd',
                    borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontSize: '12px',
                  }}
                  onMouseOver={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#e0e0e0'; }}
                  onMouseOut={(e) => { (e.currentTarget as HTMLButtonElement).style.backgroundColor = '#f0f0f0'; }}
                >
                  {example}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={executeQuery}
            disabled={isLoading || !query.trim() || !data}
            style={{
              width: '100%', padding: '10px', backgroundColor: '#2196F3', color: 'white',
              border: 'none', borderRadius: '4px',
              cursor: isLoading || !query.trim() || !data ? 'not-allowed' : 'pointer',
              opacity: isLoading || !query.trim() || !data ? 0.6 : 1,
            }}
          >
            {isLoading ? 'Exécution en cours...' : 'Exécuter la requête'}
          </button>

          {isLoading && <LoadingSpinner label="Traitement de la requête..." />}
        </div>
      ) : (
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <div style={{ padding: '12px', backgroundColor: '#e3f2fd', borderRadius: '4px', marginBottom: '16px' }}>
            <p style={{ margin: '0 0 8px 0', fontSize: '12px', fontWeight: 'bold' }}>Requête : {result.query}</p>
            <div style={{ display: 'flex', gap: '16px', fontSize: '12px' }}>
              <div>
                <span style={{ color: '#666' }}>Résultats :</span>
                <span style={{ fontWeight: 'bold', marginLeft: '4px' }}>{result.results.rowCount} lignes</span>
              </div>
              <div>
                <span style={{ color: '#666' }}>Confiance :</span>
                <span style={{ fontWeight: 'bold', marginLeft: '4px', color: getConfidenceColor(result.confidence) }}>
                  {(result.confidence * 100).toFixed(0)}%
                </span>
              </div>
              <div>
                <span style={{ color: '#666' }}>Durée :</span>
                <span style={{ fontWeight: 'bold', marginLeft: '4px' }}>{result.executionTime}ms</span>
              </div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <button
              onClick={() => setShowInterpretation(!showInterpretation)}
              style={{
                width: '100%', padding: '10px', backgroundColor: '#f5f5f5', border: '1px solid #ddd',
                borderRadius: '4px', cursor: 'pointer', textAlign: 'left', fontWeight: 'bold',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}
            >
              <span>🔍 Interprétation de la requête</span>
              <span>{showInterpretation ? '▼' : '▶'}</span>
            </button>

            {showInterpretation && (
              <div style={{ padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px' }}>
                <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: '#666' }}>
                  {result.interpretation.naturalLanguageExplanation}
                </p>
                {result.interpretation.detectedColumns.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: 'bold' }}>Colonnes :</p>
                    <p style={{ margin: '0', fontSize: '11px', color: '#666' }}>{result.interpretation.detectedColumns.join(', ')}</p>
                  </div>
                )}
                {result.interpretation.detectedFilters.length > 0 && (
                  <div style={{ marginBottom: '8px' }}>
                    <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: 'bold' }}>Filtres :</p>
                    {result.interpretation.detectedFilters.map((f: QueryFilter, idx: number) => (
                      <p key={idx} style={{ margin: '2px 0', fontSize: '11px', color: '#666' }}>
                        {f.column} {f.operator} {String(f.value)}
                      </p>
                    ))}
                  </div>
                )}
                {result.interpretation.detectedAggregations.length > 0 && (
                  <div>
                    <p style={{ margin: '0 0 4px 0', fontSize: '11px', fontWeight: 'bold' }}>Agrégations :</p>
                    {result.interpretation.detectedAggregations.map((a, idx) => (
                      <p key={idx} style={{ margin: '2px 0', fontSize: '11px', color: '#666' }}>
                        {a.type} de {a.column}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div style={{ padding: '12px', backgroundColor: '#fafafa', borderRadius: '4px', marginBottom: '16px' }}>
            <h4 style={{ margin: '0 0 12px 0' }}>📊 Résultats</h4>
            {renderResultsTable()}
          </div>

          {result.results.aggregations && Object.keys(result.results.aggregations).length > 0 && (
            <div style={{ padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px', marginBottom: '16px' }}>
              <h4 style={{ margin: '0 0 8px 0' }}>📈 Agrégations</h4>
              {Object.entries(result.results.aggregations).map(([key, value]) => (
                <div key={key} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', borderBottom: '1px solid #eee' }}>
                  <span>{key} :</span>
                  <span style={{ fontWeight: 'bold' }}>{typeof value === 'number' ? value.toFixed(2) : value}</span>
                </div>
              ))}
            </div>
          )}

          <button
            onClick={() => setResult(null)}
            style={{
              width: '100%', padding: '10px', backgroundColor: '#f5f5f5', color: '#333',
              border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer',
            }}
          >
            ← Nouvelle requête
          </button>
        </div>
      )}
    </div>
  );
};
